import React, { memo, useMemo, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  formatNumberToDisplayedString,
  getFxCfdQuantityStep,
  getPriceStepByServiceId,
  getServiceQuantityUnit,
} from 'shared-modules/services';
import {
  ORDER_TYPES,
  COUNT_INPUT,
  PRICE_INPUT,
  SETTLEMENT_LIMIT_PRICE_INPUT,
  SETTLEMENT_STOP_PRICE_INPUT,
  TIME_INPUT,
  SETTLEMENT_TIME_INPUT,
  SETTLEMENT_DATE_INPUT,
  DATE_INPUT,
  SETTLEMENT_EXPIRATION_TYPE_INPUT,
} from 'shared-modules/constants/manualTrade';
import {
  OPTIONS_BUY_SELL,
  OPTIONS_ORDER_METHOD,
  OPTIONS_EXPIRATION_TYPE,
  BUY_SELL_MAIN,
  EXPIRATION_TYPE_MAIN,
  IFO_SETTLEMENT_CUSTOM_SELECTOR,
  IFO_ORDER_METHOD_SELECTOR,
  IFO_EXPIRATION_DATE_SELECTOR,
  IFO_SETTLEMENT_EXPIRATION_DATE_SELECTOR,
  ETF,
  ORDER_METHOD_MAIN,
  REQUIRED_MARGIN_LABEL,
  REQUIRED_MARGIN_TOOLTIP,
} from 'shared-modules/constants';
import {
  useTimeChangingHandler,
  useInputCreateOrder,
  useBuySellInputCreateOrder,
  useManualTradeSelectedInstrumentSettings,
  useOrderValidationErrors,
  useValidateSelectedManualTradeOrder,
  useValidateManualTradeInput,
  useBuySellOptionRestrictions,
  useCalculateManualMargin,
} from 'shared-modules/services/hooks';
import { MARKUPS_URL } from 'shared-modules/config';
import { useIsMarkupTarget } from 'shared-modules/hooks/markup';
import { SELECT_WIDTH, TIME_INPUT_WIDTH, DATEPICKER_WIDTH } from '../../constants';
import { toggleUserSettingNewOrderSkipConfirmationRequest } from '../../../../redux/actions';
import CustomSelect from '../../../../components/CustomSelect';
import CustomInput from '../../../../components/CustomInput';
import CustomDatePicker from '../../../../components/CustomDatePicker';
import LabeledSwitch from '../../../../components/LabeledSwitch';
import styles from './sideMenuOptionsIfo.module.scss';
import { InputNumber, Switch, BuySellGroupButton } from '../../../../components';
import { QuantityLabel } from '../../../../components/QuantityLabel';
import { InfoTooltip } from '../../../../components/InfoTooltip';

const SideMenuOcoOptions = () => {
  const dispatch = useDispatch();

  const serviceId = useSelector((state) => state.auth.serviceId);
  const quantityUnit = getServiceQuantityUnit(serviceId);
  const withoutConfirmation = useSelector((state) => state.settings[serviceId].skipNewOrderConfirmation);

  const instrumentId = useSelector((state) => state.manualTrade.selectedInstrumentId[serviceId]);

  const currentDate = useMemo(() => new Date(), []);

  const validateSelectedManualTradeOrder = useValidateSelectedManualTradeOrder();
  const validateSelectedManualTradeOrderRef = useRef({});
  useEffect(() => {
    validateSelectedManualTradeOrderRef.current = { validateSelectedManualTradeOrder };
  }, [validateSelectedManualTradeOrder]);

  const validateManualTradeInput = useValidateManualTradeInput();
  const validateManualTradeInputRef = useRef({});
  useEffect(() => {
    validateManualTradeInputRef.current = { validateManualTradeInput };
  }, [validateManualTradeInput]);

  const validationErrors = useOrderValidationErrors(ORDER_TYPES.IFO.name);

  const { quantityPrecision, pricePrecision, allowBuyFlg, allowSellFlg, quantityUnitConvFactor } =
    useManualTradeSelectedInstrumentSettings();

  const isETF = serviceId === ETF;

  const quantityIsInteger = isETF && quantityPrecision >= 1;
  const priceStep = useMemo(
    () => getPriceStepByServiceId(serviceId, instrumentId, pricePrecision),
    [pricePrecision, instrumentId, serviceId],
  );
  const priceIsInteger = useMemo(() => isETF && priceStep >= 1, [isETF, priceStep]);

  const handleChangeWithoutConfirmation = useCallback(() => {
    dispatch(toggleUserSettingNewOrderSkipConfirmationRequest());
  }, [dispatch]);

  const [buySell, changeBuySell] = useBuySellInputCreateOrder(ORDER_TYPES.IFO.name);
  useBuySellOptionRestrictions(allowBuyFlg, allowSellFlg, buySell, changeBuySell);
  const [orderMethod, changeOrderMethod] = useInputCreateOrder(
    ORDER_TYPES.IFO.name,
    ORDER_TYPES.IFO.inputs.ORDER_METHOD,
  );
  const [count, changeCount] = useInputCreateOrder(
    ORDER_TYPES.IFO.name,
    ORDER_TYPES.IFO.inputs.COUNT,
    quantityPrecision,
    true,
  );
  const [price, changePrice] = useInputCreateOrder(ORDER_TYPES.IFO.name, ORDER_TYPES.IFO.inputs.PRICE, pricePrecision);
  const [expirationType, changeExpirationType] = useInputCreateOrder(
    ORDER_TYPES.IFO.name,
    ORDER_TYPES.IFO.inputs.EXPIRATION_TYPE,
    null,
    true,
  );
  const [selectedDate, setSelectedDate] = useInputCreateOrder(
    ORDER_TYPES.IFO.name,
    ORDER_TYPES.IFO.inputs.SELECTED_DATE,
    null,
    true,
  );
  const [selectedTime, setSelectedTime] = useInputCreateOrder(
    ORDER_TYPES.IFO.name,
    ORDER_TYPES.IFO.inputs.SELECTED_TIME,
    null,
    true,
  );

  const onTimeChange = useTimeChangingHandler(setSelectedTime);

  const settlementBuySell = useMemo(
    () => (buySell === BUY_SELL_MAIN.SELL.ID ? BUY_SELL_MAIN.BUY.ID : BUY_SELL_MAIN.SELL.ID),
    [buySell],
  );
  const changeSettlementBuySell = useCallback(() => {}, []);
  const [settlementLimitPrice, changeSettlementLimitPrice] = useInputCreateOrder(
    ORDER_TYPES.IFO.name,
    ORDER_TYPES.IFO.inputs.SETTLEMENT_LIMIT_PRICE,
    pricePrecision,
  );
  const [settlementStopPrice, changeSettlementStopPrice] = useInputCreateOrder(
    ORDER_TYPES.IFO.name,
    ORDER_TYPES.IFO.inputs.SETTLEMENT_STOP_PRICE,
    pricePrecision,
  );
  const [settlementExpirationType, changeSettlementExpirationType] = useInputCreateOrder(
    ORDER_TYPES.IFO.name,
    ORDER_TYPES.IFO.inputs.SETTLEMENT_EXPIRATION_TYPE,
  );
  const [settlementSelectedDate, setSettlementSelectedDate] = useInputCreateOrder(
    ORDER_TYPES.IFO.name,
    ORDER_TYPES.IFO.inputs.SETTLEMENT_SELECTED_DATE,
  );
  const [settlementSelectedTime, setSettlementSelectedTime] = useInputCreateOrder(
    ORDER_TYPES.IFO.name,
    ORDER_TYPES.IFO.inputs.SETTLEMENT_SELECTED_TIME,
  );

  const onSettlementTimeChange = useTimeChangingHandler(setSettlementSelectedTime);

  const calculatedMargin = useCalculateManualMargin(count, price);

  const { errorMessage: settlementExpireTypeErrorMessage, hasValidationError: settlementExpireTypeHasError } =
    useMemo(() => {
      const expireTypeValidObject = validationErrors.find(
        ({ inputName }) => inputName === SETTLEMENT_EXPIRATION_TYPE_INPUT,
      );
      if (expireTypeValidObject?.errorMessage) {
        return { errorMessage: expireTypeValidObject.errorMessage, hasValidationError: true };
      }
      return {};
    }, [validationErrors]);

  const priceRef = useRef({});
  useEffect(() => {
    priceRef.current = { price, settlementLimitPrice, settlementStopPrice };
  }, [price, settlementLimitPrice, settlementStopPrice]);

  // validate all values on orderMethod change
  useEffect(() => {
    const { validateSelectedManualTradeOrder: validate } = validateSelectedManualTradeOrderRef.current;
    if (validate) {
      validate({ changedType: orderMethod });
    }
  }, [orderMethod]);

  const isNewOrderMarkupTarget = useIsMarkupTarget({
    instrumentId,
    orderType: orderMethod,
    orderAmount: count,
  });

  const isSettlementOrderMarkupTarget = useIsMarkupTarget({
    instrumentId,
    orderType: ORDER_METHOD_MAIN.STOP_LIMIT.ID,
    orderAmount: count,
  });

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>新規</div>
      <div className={styles.buySellRow}>
        売買
        <BuySellGroupButton
          instrumentId={instrumentId}
          buySell={buySell}
          onChange={changeBuySell}
          disabledSell={!allowSellFlg}
          disabledBuy={!allowBuyFlg}
        />
      </div>
      <div className={styles.orderMethodRow}>
        注文条件
        <LabeledSwitch
          activeItemId={orderMethod}
          onChange={changeOrderMethod}
          options={OPTIONS_ORDER_METHOD}
          itemClassName={styles.itemClassName}
          dataCustomSelector={IFO_ORDER_METHOD_SELECTOR}
        />
      </div>
      <div className={styles.countRow}>
        <QuantityLabel
          serviceId={serviceId}
          quantityUnit={quantityUnit}
          quantityUnitConvFactor={quantityUnitConvFactor}
        />
        <InputNumber
          value={count}
          name={COUNT_INPUT}
          errorMessages={validationErrors}
          onChange={changeCount}
          type="number"
          step={isETF ? quantityPrecision : getFxCfdQuantityStep}
          onlyIntegerAllowed={quantityIsInteger}
          withErrorTooltip
        />
      </div>
      {isNewOrderMarkupTarget && (
        <div className={styles.makeupsWarningRow}>
          <div className={styles.inner}>
            逆指値注文を設定し、かつ数量が100万通貨を超える場合、約定価格に大口マークアップが加算されます。詳しくは
            <a className={styles.button} href={MARKUPS_URL} target="_blank" rel="noopener noreferrer">
              こちら
            </a>
          </div>
        </div>
      )}
      <div className={styles.necessaryMarginRow}>
        <div className={styles.label}>
          {REQUIRED_MARGIN_LABEL}
          <InfoTooltip message={REQUIRED_MARGIN_TOOLTIP} />
        </div>
        <div className={styles.necessaryMargin}>
          {formatNumberToDisplayedString({ value: calculatedMargin, withoutPlus: true })}
        </div>
      </div>
      <div className={styles.priceRow}>
        価格
        <InputNumber
          value={price}
          name={PRICE_INPUT}
          errorMessages={validationErrors}
          onChange={changePrice}
          type="number"
          step={priceStep}
          onlyIntegerAllowed={priceIsInteger}
          withErrorTooltip
        />
      </div>
      <div className={styles.expirationTypeRow}>
        有効期限
        <CustomSelect
          instanceId="side-menu-ifo-expiration-date-01"
          selectItemId={expirationType}
          onChange={changeExpirationType}
          options={OPTIONS_EXPIRATION_TYPE}
          width={SELECT_WIDTH}
          dataCustomSelector={IFO_EXPIRATION_DATE_SELECTOR}
          isDarker
        />
      </div>
      {expirationType === EXPIRATION_TYPE_MAIN.CUSTOM.ID && (
        <>
          <div className={styles.datePickerRow}>
            日付
            <CustomDatePicker
              width={DATEPICKER_WIDTH}
              date={selectedDate}
              onChange={setSelectedDate}
              minDate={currentDate}
              name={DATE_INPUT}
              errorMessages={validationErrors}
              popperPlacement="auto"
              isTaller
              disableSundays
            />
          </div>
          <div className={styles.timePickerRow}>
            時刻
            <CustomInput
              value={selectedTime}
              onChange={onTimeChange}
              width={TIME_INPUT_WIDTH}
              name={TIME_INPUT}
              errorMessages={validationErrors}
              withErrorTooltip
              placeholder="00:00"
              isEndAligned
            />
          </div>
        </>
      )}
      <div className={styles.divider} />
      <div className={styles.settlementOrderTitle}>決済</div>
      <div className={styles.settlementBuySellRow}>
        決済売買
        <LabeledSwitch
          activeItemId={settlementBuySell}
          onChange={changeSettlementBuySell}
          options={OPTIONS_BUY_SELL}
          itemClassName={styles.itemClassName}
          showLockIcon
          isNotSelectable
          dataCustomSelector={IFO_SETTLEMENT_CUSTOM_SELECTOR}
        />
      </div>
      <div className={styles.settlementCountRow}>
        <QuantityLabel
          serviceId={serviceId}
          quantityUnit={quantityUnit}
          quantityUnitConvFactor={quantityUnitConvFactor}
        />
        <InputNumber value={count} onChange={changeCount} type="number" disabled />
      </div>
      {isSettlementOrderMarkupTarget && (
        <div className={styles.makeupsWarningRow}>
          <div className={styles.inner}>
            逆指値注文を設定し、かつ数量が100万通貨を超える場合、約定価格に大口マークアップが加算されます。詳しくは
            <a className={styles.button} href={MARKUPS_URL} target="_blank" rel="noopener noreferrer">
              こちら
            </a>
          </div>
        </div>
      )}
      <div className={styles.settlementLimitPriceRow}>
        指値価格
        <InputNumber
          value={settlementLimitPrice}
          name={SETTLEMENT_LIMIT_PRICE_INPUT}
          errorMessages={validationErrors}
          onChange={changeSettlementLimitPrice}
          type="number"
          step={priceStep}
          onlyIntegerAllowed={priceIsInteger}
          withErrorTooltip
        />
      </div>
      <div className={styles.settlementStopPriceRow}>
        逆指値価格
        <InputNumber
          value={settlementStopPrice}
          name={SETTLEMENT_STOP_PRICE_INPUT}
          errorMessages={validationErrors}
          onChange={changeSettlementStopPrice}
          type="number"
          step={priceStep}
          onlyIntegerAllowed={priceIsInteger}
          withErrorTooltip
        />
      </div>
      <div className={styles.settlementExpirationTypeRow}>
        有効期限
        <CustomSelect
          instanceId="side-menu-ifo-expiration-date-02"
          selectItemId={settlementExpirationType}
          onChange={changeSettlementExpirationType}
          options={OPTIONS_EXPIRATION_TYPE}
          errorMessage={settlementExpireTypeErrorMessage}
          hasError={settlementExpireTypeHasError}
          width={SELECT_WIDTH}
          withErrorTooltip
          dataCustomSelector={IFO_SETTLEMENT_EXPIRATION_DATE_SELECTOR}
          menuPlacement="top"
          isDarker
        />
      </div>
      {settlementExpirationType === EXPIRATION_TYPE_MAIN.CUSTOM.ID && (
        <>
          <div className={styles.datePickerRow}>
            日付
            <CustomDatePicker
              width={DATEPICKER_WIDTH}
              date={settlementSelectedDate}
              onChange={setSettlementSelectedDate}
              minDate={currentDate}
              name={SETTLEMENT_DATE_INPUT}
              errorMessages={validationErrors}
              popperPlacement="auto"
              isTaller
              disableSundays
            />
          </div>
          <div className={styles.timePickerRow}>
            時刻
            <CustomInput
              value={settlementSelectedTime}
              onChange={onSettlementTimeChange}
              width={TIME_INPUT_WIDTH}
              name={SETTLEMENT_TIME_INPUT}
              errorMessages={validationErrors}
              withErrorTooltip
              placeholder="00:00"
              isEndAligned
            />
          </div>
        </>
      )}
      <div className={styles.countRow}>
        確認省略
        <Switch checked={withoutConfirmation} onChange={handleChangeWithoutConfirmation} />
      </div>
    </div>
  );
};

export default memo(SideMenuOcoOptions);
